
import React, { useState, useMemo } from 'react';
import { TrendingUp, DollarSign, Building, Users, ArrowUpRight, Wallet, BarChart3, AlertCircle } from 'lucide-react';
import type { Receivable, Bill } from '@repo/shared-types';
import { useOwnerMetrics } from '../../hooks/queries/useOwnerMetrics';
import { formatCurrency, formatNumber, formatPercentage } from '@repo/shared-utils';
import { useCurrentOwnerId } from '../../hooks/queries/useEntityId';
import { useNavigate } from '../../routes';
import { getAppNow } from '@/utils/demoClock';

type DateRange = 'YTD' | 'MTD' | '3M' | '6M' | '12M' | 'Custom';

export const OwnerDashboardView: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>('YTD');

  // Get owner ID from user account (with linking support)
  const { data: currentOwnerId } = useCurrentOwnerId();

  const { data: metrics, isLoading } = useOwnerMetrics(currentOwnerId || undefined);

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!metrics) return { roi: '0.0%', expenseRatio: '0.0%', revenueGrowth: 0, noiGrowth: 0 };
    const { totalRevenue, totalExpenses, roi } = metrics;
    const expenseRatio = totalRevenue > 0 ? ((totalExpenses / totalRevenue) * 100) : 0;

    // Calculate growth: compare current period with previous period based on dateRange
    const now = getAppNow();
    const getDateRangeDates = (range: DateRange) => {
      const start = new Date(now.getTime());
      switch (range) {
        case 'YTD':
          start.setMonth(0, 1);
          break;
        case 'MTD':
          start.setDate(1);
          break;
        case '3M':
          start.setMonth(now.getMonth() - 3);
          break;
        case '6M':
          start.setMonth(now.getMonth() - 6);
          break;
        case '12M':
          start.setMonth(now.getMonth() - 12);
          break;
        default:
          start.setMonth(0, 1);
      }
      return { start, end: now };
    };

    const { start: currentStart, end: currentEnd } = getDateRangeDates(dateRange);

    // Calculate previous period (same length, ending at currentStart)
    const periodLength = currentEnd.getTime() - currentStart.getTime();
    const previousEnd = new Date(currentStart.getTime() - 1); // Day before current period starts
    const previousStart = new Date(previousEnd.getTime() - periodLength);

    // Filter receivables and bills for current period
    const currentPeriodReceivables = metrics.receivables.filter((r) => {
      if (!r.date) return false;
      const date = new Date(r.date);
      return date >= currentStart && date <= currentEnd;
    });
    const currentPeriodBills = metrics.bills.filter((b) => {
      if (!b.date) return false;
      const date = new Date(b.date);
      return date >= currentStart && date <= currentEnd;
    });

    // Filter receivables and bills for previous period
    const previousPeriodReceivables = metrics.receivables.filter((r) => {
      if (!r.date) return false;
      const date = new Date(r.date);
      return date >= previousStart && date <= previousEnd;
    });
    const previousPeriodBills = metrics.bills.filter((b) => {
      if (!b.date) return false;
      const date = new Date(b.date);
      return date >= previousStart && date <= previousEnd;
    });

    // Calculate revenue and NOI for both periods
    const currentPeriodRevenue = currentPeriodReceivables.reduce((sum: number, r: Receivable) => sum + (r.amountCents ?? 0) / 100, 0);
    const previousPeriodRevenue = previousPeriodReceivables.reduce((sum: number, r: Receivable) => sum + (r.amountCents ?? 0) / 100, 0);
    const currentPeriodExpenses = currentPeriodBills.reduce((sum: number, b: Bill) => sum + (b.amountCents ?? 0) / 100, 0);
    const previousPeriodExpenses = previousPeriodBills.reduce((sum: number, b: Bill) => sum + (b.amountCents ?? 0) / 100, 0);
    const currentPeriodNOI = currentPeriodRevenue - currentPeriodExpenses;
    const previousPeriodNOI = previousPeriodRevenue - previousPeriodExpenses;

    // Calculate growth percentages
    const revenueGrowth = previousPeriodRevenue > 0
      ? ((currentPeriodRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100
      : 0;
    const noiGrowth = previousPeriodNOI === 0
      ? 0
      : ((currentPeriodNOI - previousPeriodNOI) / Math.abs(previousPeriodNOI)) * 100;

    // `calculateROI` + the local `expenseRatio` both already return values on a
    // 0-100 scale. Pass asDecimal=false so `formatPercentage` does not multiply
    // by 100 again (which previously produced numbers like "1928.1%").
    return {
      roi: formatPercentage(roi, 1, false),
      expenseRatio: formatPercentage(expenseRatio, 1, false),
      revenueGrowth: Math.round(revenueGrowth * 10) / 10, // Round to 1 decimal
      noiGrowth: Math.round(noiGrowth * 10) / 10 // Round to 1 decimal
    };
  }, [metrics, dateRange]);

  // Early return AFTER all hooks
  if (isLoading || !metrics) return <div>Loading owner profile for {currentOwnerId}...</div>;

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Owner Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Portfolio performance, maintenance approvals (mobile-friendly), and Stripe Connect distributions. Year-end
            1099 reporting follows Stripe when your account is on Connect—use monthly statements for exports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          >
            <option value="YTD">Year to Date</option>
            <option value="MTD">Month to Date</option>
            <option value="3M">Last 3 Months</option>
            <option value="6M">Last 6 Months</option>
            <option value="12M">Last 12 Months</option>
          </select>
        </div>
      </div>

      {metrics.pendingApprovalTicketCount > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" size={22} aria-hidden />
              <div>
                <h2 className="text-sm font-bold text-amber-950 dark:text-amber-100">Repairs awaiting your approval</h2>
                <p className="mt-1 text-sm text-amber-900/90 dark:text-amber-100/90">
                  You have {metrics.pendingApprovalTicketCount}{' '}
                  {metrics.pendingApprovalTicketCount === 1 ? 'work order' : 'work orders'} in Pending Approval. Open Maintenance
                  and use the quick filter &quot;Pending Approval&quot; to review and approve or reject.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/app/maintenance')}
              className="shrink-0 rounded-lg bg-amber-900 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 dark:bg-amber-200 dark:text-amber-950 dark:hover:bg-amber-100"
            >
              Go to maintenance
            </button>
          </div>
        </div>
      ) : null}

      {/* Key Metrics — F-pattern: #1 KPI top-left (Net Income), then Occupancy, Cash Flow, Total Properties */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Net Income ({dateRange})</span>
            <DollarSign size={20} className="text-emerald-500" aria-hidden />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(metrics.noi / 1000, 1)}k</div>
          <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center mt-1">
            <ArrowUpRight size={12} className="mr-1" /> {performanceMetrics.noiGrowth >= 0 ? '+' : ''}{performanceMetrics.noiGrowth}% vs previous period
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Occupancy</span>
            <Users size={20} className={metrics.occupancyRate >= 90 ? 'text-emerald-500' : 'text-amber-500'} aria-hidden />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.occupancyRate}%</div>
          <div className={`text-xs mt-1 ${metrics.occupancyRate >= 90 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {metrics.occupancyRate >= 90 ? 'On track' : 'Attention needed'}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Cash Flow (YTD)</span>
            <Wallet size={20} className="text-slate-400" aria-hidden />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(metrics.distributionsPaid, { showCents: false })}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Next payout: Nov 1
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Properties</span>
            <Building size={20} className="text-slate-400" aria-hidden />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.totalProperties}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{metrics.totalUnits} Units Total</div>
        </div>
      </div>

      {/* Performance Metrics — traffic-light: green = healthy, amber = attention, no decorative red */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">ROI</span>
            <TrendingUp size={18} className="text-emerald-500" aria-hidden />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{performanceMetrics.roi}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">Return on Investment</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Expense Ratio</span>
            <BarChart3 size={18} className="text-amber-500" aria-hidden />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{performanceMetrics.expenseRatio}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">Expenses / Revenue</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Revenue Growth</span>
            <ArrowUpRight size={18} className={performanceMetrics.revenueGrowth >= 0 ? 'text-emerald-500' : 'text-amber-500'} aria-hidden />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">{performanceMetrics.revenueGrowth >= 0 ? '+' : ''}{performanceMetrics.revenueGrowth}%</div>
          <div className={`text-xs mt-2 ${performanceMetrics.revenueGrowth >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            vs previous period
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="font-bold text-slate-900 dark:text-white">Portfolio detail</h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Figures above are derived from the same receivables, bills, and occupancy data as your owner statements — not from
          illustrative charts. For period-accurate income and expenses, use{' '}
          <button
            type="button"
            className="font-medium text-brand-600 underline hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            onClick={() => navigate('/app/owner/statements')}
          >
            Statements
          </button>
          {'.'}
        </p>
      </div>
    </div>
  );
};


